import React, { useState } from 'react'
import { loadStripe } from '@stripe/stripe-js'
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js'
import { Dialog, DialogPanel, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import { convertMeetingDateTimeToGMT } from '../utils/timezoneUtils'

// Extend Window interface for custom properties
declare global {
  interface Window {
    lastSuccessfulPaymentId?: string;
  }
}

// Initialize Stripe with your publishable key
const publishableKey = import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY || 'pk_test_...'
if (!publishableKey || publishableKey === 'pk_test_...') {
  console.warn('Stripe publishable key not configured. Set VITE_STRIPE_PUBLISHABLE_KEY environment variable.')
}
const stripePromise = loadStripe(publishableKey)

interface PaymentModalProps {
  open: boolean
  onClose: () => void
  onSuccess: () => void
  amount: number // Amount in dollars
  professionalId: string
  professionalName: string
  sessionDetails: {
    date: string
    time: string
    duration: number
  }
  userTimezone: string
}

interface CheckoutFormProps {
  amount: number
  professionalId: string
  professionalName: string
  sessionDetails: {
    date: string
    time: string
    duration: number
  }
  onSuccess: () => void
  onClose: () => void
  clientSecret: string | null
}


function CheckoutForm({ amount, professionalName, sessionDetails, onSuccess, onClose, clientSecret }: CheckoutFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isLoading, setIsLoading] = useState(false)
  const [isPaymentSuccessful, setIsPaymentSuccessful] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  // Guard against null clientSecret
  if (!clientSecret) {
    return (
      <div className="flex items-center justify-center p-6">
        <div className="text-white/50">Loading payment...</div>
      </div>
    )
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()

    if (!stripe || !elements) {
      return
    }

    setIsLoading(true)
    setErrorMessage(null)

    try {
      // Confirm payment with Stripe using the existing client secret
      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/booking-success`,
        },
        redirect: 'if_required'
      })

      if (error) {
        setErrorMessage(error.message || 'An error occurred during payment')
        setIsLoading(false)
      } else {
        // Payment succeeded - now confirm the booking
        try {
          // Get the payment intent ID from the client secret
          const paymentIntentId = clientSecret.split('_secret_')[0]

          // Store the payment intent ID for BookingCalendar to use
          window.lastSuccessfulPaymentId = paymentIntentId

        } catch (bookingError) {
          console.error('Failed to confirm booking in database:', bookingError)
          // Continue anyway - the payment was successful
        }

        // Keep loading state active during navigation
        setIsPaymentSuccessful(true)
        onSuccess() // This will trigger navigation while spinner continues
      }

    } catch (error: any) {
      setErrorMessage(error.message || 'Payment failed. Please try again.')
      setIsLoading(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      {/* Booking Summary */}
      <div className="panel-soft p-4">
        <div className="summary-row">
          <span className="summary-lbl">WITH</span>
          <span className="font-medium">{professionalName}</span>
        </div>
        <div className="summary-row">
          <span className="summary-lbl">DATE</span>
          <span>{sessionDetails.date}</span>
        </div>
        <div className="summary-row">
          <span className="summary-lbl">TIME</span>
          <span>{sessionDetails.time}</span>
        </div>
        <div className="summary-row">
          <span className="summary-lbl">SESSION</span>
          <span>30-min · video call</span>
        </div>
        <div className="summary-row">
          <span className="summary-lbl">TOTAL</span>
          <span className="font-semibold tracking-tight text-[15px]">${amount.toFixed(2)}</span>
        </div>
      </div>

      {/* Security badge */}
      <div className="flex items-center gap-2 panel-soft p-3 text-[12px] text-white/50">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="text-white/80 shrink-0">
          <rect x="3" y="11" width="18" height="10" rx="2"/><path d="M7 11V8a5 5 0 0 1 10 0v3"/>
        </svg>
        Encrypted by Stripe. We never see your card number.
      </div>

      {/* Payment Element */}
      <div className="space-y-3">
        <div className="label">Payment details</div>
        <div className="rounded-xl overflow-hidden" style={{ boxShadow: 'inset 0 0 0 1px hsl(0 0% 20%)' }}>
          <PaymentElement />
        </div>
      </div>

      {errorMessage && (
        <div className="panel-soft p-4 text-[12.5px]" style={{ background: 'rgba(255,138,115,0.04)' }}>
          <div className="flex items-start gap-2.5">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
            <span className="text-[#ff8a73]">{errorMessage}</span>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex flex-col gap-2 pt-2">
        <button
          type="submit"
          disabled={!stripe || isLoading}
          className="btn btn-primary w-full justify-center !py-3"
          style={{ opacity: !stripe || isLoading ? 0.45 : 1 }}
        >
          {isLoading ? (
            <span className="flex items-center gap-2">
              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/></svg>
              {isPaymentSuccessful ? 'Booking session...' : 'Processing payment...'}
            </span>
          ) : (
            <>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12l5 5L20 6"/></svg>
              Pay ${amount.toFixed(2)}
            </>
          )}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="btn btn-ghost w-full justify-center"
          disabled={isLoading}
        >
          Cancel
        </button>
      </div>
    </form>
  )
}

export default function StripePaymentModal({
  open,
  onClose,
  onSuccess,
  amount,
  professionalId,
  professionalName,
  sessionDetails,
  userTimezone
}: PaymentModalProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [loadingPaymentIntent, setLoadingPaymentIntent] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isCreatingPaymentIntent, setIsCreatingPaymentIntent] = useState(false)

  // Create payment intent when modal opens
  React.useEffect(() => {
    if (!open || clientSecret || !professionalId || isCreatingPaymentIntent) return

    const createPaymentIntent = async () => {
      try {
        setIsCreatingPaymentIntent(true)
        setLoadingPaymentIntent(true)
        setError(null)

        // Convert date/time to GMT using the timezone passed from BookingCalendar
        const gmtDateTime = convertMeetingDateTimeToGMT(
          sessionDetails.date,
          sessionDetails.time,
          userTimezone
        )

        const { httpsCallable } = await import('firebase/functions')
        const { functions } = await import('../lib/firebase')
        const createPaymentIntentFn = httpsCallable(functions, 'createPaymentIntent')

        const result = await createPaymentIntentFn({
          amount: amount,
          professionalId: professionalId,
          sessionDetails: {
            ...sessionDetails,
            gmtDateTime: gmtDateTime
          },
          userTimezone: userTimezone
        })

        const data = result.data as any
        setClientSecret(data.clientSecret)
      } catch (error: any) {
        console.error('Payment intent creation failed:', error)
        setError(error.message || 'Failed to initialize payment')
      } finally {
        setLoadingPaymentIntent(false)
        setIsCreatingPaymentIntent(false)
      }
    }

    createPaymentIntent()
  }, [open, amount, professionalId, sessionDetails.date, sessionDetails.time, sessionDetails.duration, userTimezone])

  // Reset client secret when modal closes to prevent mutation error
  React.useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setIsCreatingPaymentIntent(false)
      setError(null)
    }
  }, [open])

  const options = clientSecret ? {
    clientSecret,
    appearance: {
      theme: 'night' as const,
      variables: {
        colorPrimary: '#ffffff',
        colorBackground: 'hsl(0, 0%, 7%)',
        colorText: '#ffffff',
        colorTextSecondary: 'hsl(0, 0%, 65%)',
        colorTextPlaceholder: 'hsl(0, 0%, 45%)',
        colorDanger: '#ff8a73',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSizeBase: '13.5px',
        spacingUnit: '4px',
        borderRadius: '10px',
        colorIconCardCvc: 'hsl(0, 0%, 65%)',
      },
      rules: {
        '.Input': {
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid hsl(0, 0%, 20%)',
          boxShadow: 'none',
          padding: '11px 13px',
          transition: 'border-color 0.15s, background-color 0.15s',
        },
        '.Input:focus': {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
          border: '1px solid hsl(0, 0%, 50%)',
          boxShadow: 'none',
        },
        '.Label': {
          fontSize: '12px',
          fontWeight: '500',
          color: '#ffffff',
          marginBottom: '6px',
        },
        '.Tab': {
          backgroundColor: 'rgba(255, 255, 255, 0.02)',
          border: '1px solid hsl(0, 0%, 20%)',
          boxShadow: 'none',
        },
        '.Tab--selected': {
          backgroundColor: '#ffffff',
          color: '#000000',
          border: 'none',
        },
        '.Tab:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.04)',
        },
      },
    },
  } : null

  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[120]" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0" style={{ background: 'rgba(0,0,0,0.62)', backdropFilter: 'blur(12px) saturate(120%)' }} />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-200"
              enterFrom="opacity-0 translate-y-2 scale-[0.985]"
              enterTo="opacity-100 translate-y-0 scale-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100 translate-y-0 scale-100"
              leaveTo="opacity-0 translate-y-2 scale-[0.985]"
            >
              <DialogPanel
                className="w-full max-w-md overflow-hidden rounded-2xl md:rounded-[20px] relative"
                style={{ background: 'hsl(0 0% 5%)', border: '1px solid hsl(0 0% 20%)', boxShadow: '0 30px 80px rgba(0,0,0,0.55)' }}
              >
                {/* Close button */}
                <button
                  onClick={onClose}
                  className="absolute top-3 right-3 md:top-[18px] md:right-[18px] w-[34px] h-[34px] rounded-full inline-flex items-center justify-center z-10 cursor-pointer transition-colors"
                  style={{ background: 'rgba(0,0,0,0.45)', boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.16)', backdropFilter: 'blur(6px)' }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
                </button>

                {/* Header */}
                <div className="px-6 md:px-8 pt-6 pb-4" style={{ borderBottom: '1px solid hsl(0 0% 20% / 0.6)' }}>
                  <div className="text-[18px] font-medium tracking-tight">Complete payment</div>
                  <p className="text-white/50 text-[12.5px] mt-1">
                    Secure payment processed by Stripe.
                  </p>
                </div>

                <div className="p-6 md:p-8">
                  {/* Payment Form */}
                  {loadingPaymentIntent ? (
                    <div className="text-center py-10">
                      <div className="animate-spin mx-auto h-8 w-8 border-2 border-white/40 border-t-white rounded-full mb-4" />
                      <p className="text-white/50 text-[13px]">Initializing payment...</p>
                    </div>
                  ) : error ? (
                    <div>
                      <div className="panel-soft p-4 text-[12.5px]" style={{ background: 'rgba(255,138,115,0.04)' }}>
                        <div className="flex items-start gap-2.5">
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#ff8a73" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="shrink-0 mt-0.5"><circle cx="12" cy="12" r="10"/><path d="M12 8v4M12 16h.01"/></svg>
                          <span className="text-[#ff8a73]">{error}</span>
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setError(null)
                          setClientSecret(null)
                        }}
                        className="btn btn-outline w-full justify-center mt-4"
                      >
                        Try again
                      </button>
                    </div>
                  ) : options ? (
                    <Elements key={clientSecret} stripe={stripePromise} options={options}>
                      <CheckoutForm
                        amount={amount}
                        professionalId={professionalId}
                        professionalName={professionalName}
                        sessionDetails={sessionDetails}
                        onSuccess={onSuccess}
                        onClose={onClose}
                        clientSecret={clientSecret}
                      />
                    </Elements>
                  ) : null}
                </div>
              </DialogPanel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  )
}
